import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import {
  reduceAgentChatEvent,
  type AgentChatItem,
} from "@/components/case-agent-chat";
import { type LiveEvent, VersionLiveClient } from "@/lib/live";

type VersionLiveContextValue = {
  versionId: string;
  caseId: string;
  liveStatus: string;
  liveByCall: Record<string, string>;
  setLiveByCall: Dispatch<SetStateAction<Record<string, string>>>;
  agentItems: AgentChatItem[];
  agentBusy: boolean;
  sendAgentPrompt: (text: string) => boolean;
  cancelAgent: () => void;
};

const VersionLiveContext = createContext<VersionLiveContextValue | null>(null);

export function VersionLiveProvider({
  caseId,
  versionId,
  children,
}: {
  caseId: string;
  versionId: string;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [liveStatus, setLiveStatus] = useState("closed");
  const [liveByCall, setLiveByCall] = useState<Record<string, string>>({});
  const [agentItems, setAgentItems] = useState<AgentChatItem[]>([]);
  const [agentBusy, setAgentBusy] = useState(false);
  const liveClientRef = useRef<VersionLiveClient | null>(null);

  useEffect(() => {
    setAgentItems([]);
    setAgentBusy(false);
    setLiveByCall({});
    const client = new VersionLiveClient({
      versionId,
      onStatus: setLiveStatus,
      onEvent: (event: LiveEvent) => {
        if (event.type === "llm_delta" && event.call_id) {
          const chunk =
            typeof event.payload?.text === "string" ? event.payload.text : "";
          if (!chunk) return;
          setLiveByCall((prev) => ({
            ...prev,
            [event.call_id!]: (prev[event.call_id!] ?? "") + chunk,
          }));
        }
        if (event.type === "llm_done" && event.call_id) {
          void queryClient.invalidateQueries({
            queryKey: ["llm-calls-step"],
          });
        }
        if (event.type === "pipeline_step") {
          void queryClient.invalidateQueries({
            queryKey: ["pipeline-runs", versionId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["pipeline-run"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["translate-status", versionId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["case", caseId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["script-meta", versionId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["phone-json", versionId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["assets", versionId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["media-readiness", versionId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["case-preview", caseId],
          });
        }
        if (
          event.type === "agent_delta" ||
          event.type === "agent_tool" ||
          event.type === "agent_tool_end" ||
          event.type === "agent_done" ||
          event.type === "agent_error"
        ) {
          setAgentItems((prev) => {
            const next = reduceAgentChatEvent(prev, event);
            if (next.busy !== null) setAgentBusy(next.busy);
            return next.items;
          });
        }
        if (
          event.type === "agent_done" ||
          event.type === "agent_error" ||
          event.type === "agent_tool_end"
        ) {
          void queryClient.invalidateQueries({
            queryKey: ["phone-json", versionId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["pipeline-run"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["assets", versionId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["llm-calls-step"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["case-preview", caseId],
          });
        }
      },
    });
    liveClientRef.current = client;
    client.connect();
    return () => {
      liveClientRef.current = null;
      client.close();
    };
  }, [versionId, queryClient, caseId]);

  const value = useMemo<VersionLiveContextValue>(
    () => ({
      versionId,
      caseId,
      liveStatus,
      liveByCall,
      setLiveByCall,
      agentItems,
      agentBusy,
      sendAgentPrompt: (text: string) =>
        liveClientRef.current?.sendAgentPrompt(text) ?? false,
      cancelAgent: () => {
        liveClientRef.current?.cancelAgent();
      },
    }),
    [
      versionId,
      caseId,
      liveStatus,
      liveByCall,
      agentItems,
      agentBusy,
    ],
  );

  return (
    <VersionLiveContext.Provider value={value}>
      {children}
    </VersionLiveContext.Provider>
  );
}

export function useVersionLive(): VersionLiveContextValue {
  const ctx = useContext(VersionLiveContext);
  if (!ctx) {
    throw new Error("useVersionLive must be used within VersionLiveProvider");
  }
  return ctx;
}
